import { selectCurrentWidgets } from '@src/features/LogicMapper/logicMapperSlice';
import { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { selectCurrentRecipe } from '@src/features/Workspace/workspaceSlice';
import { HubServiceState, WidgetType } from '@kemu-io/kemu-core/types';
import { getWidgetState } from '@src/app/recipe/utils';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import { StatelessWidgetsMap } from '@src/types/core_t';
import useIsServiceInstalled from './useIsServiceInstalled';
import { useHubContext } from '@common/context/HubContext/HubContext';


type BasicMissingService = {
  version: string;
  name: string;
  installedWithLowerVersion: boolean;
  installedWithMissingSecrets: boolean;
}


/**
 * Compares two widget sets for equality
 * @returns true if the widget set has changed
 */
const widgetSetHasChanged = (prev: StatelessWidgetsMap, next: StatelessWidgetsMap) => {
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);

  if(prevKeys.length !== nextKeys.length) {
    return false;
  }

  for(const key of prevKeys) {
    const prevHasKey = !!prev[key];
    const nextHasKey = !!next[key];

    if(prevHasKey !== nextHasKey) {
      return false;
    }
  }
  
  return true;
};


/**
 * Detects if the current recipe has Hub Services that haven't been installed. 
 * This hook only returns the basic information about the missing services.
 * @returns a list of missing services extracted from the recipe state.
 */
const useMissingHubServices = () => {
  const { services, isReady } = useHubContext();
  const [missingServices, setMissingServices] = useState<BasicMissingService[]>([]);
  const currentWidgets = useSelector(selectCurrentWidgets, widgetSetHasChanged);
  const currentRecipe = useSelector(selectCurrentRecipe);
  const getIsInstalled = useIsServiceInstalled();
  const recipeId = currentRecipe.poolId;
  const availableHubServices = services.available;

  /**
	 * Extracts all required services from the current recipe
	 */
	useEffect(() => {
		if (!currentWidgets || !isReady || !recipeId) {
			return;
		}

		const servicesNotFound: BasicMissingService[] = [];
		const serviceMap = new Map<string, { name: string; version: string }>();

    // Iterate through all widgets (gates) in each thing
    for (const widgetId in currentWidgets) {
      const widget = currentWidgets[widgetId];
      
      // Check if this is a hub service widget
      if (widget.type === WidgetType.hubService) {
        const hubServiceState = getWidgetState<HubServiceState>(widget.id, DEFAULT_THING_ID, recipeId);
        
        // Check if the service has the required service info
        if (hubServiceState.service?.name && hubServiceState.service?.version) {
          const serviceKey = `${hubServiceState.service.name}_${hubServiceState.service.version}`;
          
          // Only add if we haven't seen this exact service version before
          if (!serviceMap.has(serviceKey)) {
            serviceMap.set(serviceKey, {
              name: hubServiceState.service.name,
              version: hubServiceState.service.version,
            });

            const installStatus = getIsInstalled(hubServiceState.service.name, hubServiceState.service.version);
            if(!installStatus.installedWithSameVersion && !installStatus.installedWithHigherVersion) {
              servicesNotFound.push({
                name: hubServiceState.service.name,
                version: hubServiceState.service.version,
                installedWithLowerVersion: installStatus.installedWithLowerVersion,
                installedWithMissingSecrets: installStatus.installedWithMissingSecrets || false,
              });
            }
          }
        }
      }
    }

    setMissingServices(servicesNotFound);
		// return services;
	}, [currentWidgets, isReady, recipeId, availableHubServices]);


  return missingServices;
};

export default useMissingHubServices;